// ===========================================
// Booking Trends Chart Component
// ===========================================
// Multi-line chart showing booking trends over time
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Paper, Typography, Box, Skeleton } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { BookingTrendRecord } from '../../api/reports.api';

interface BookingTrendsChartProps {
  data?: BookingTrendRecord[];
  isLoading: boolean;
}

const BookingTrendsChart: React.FC<BookingTrendsChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Booking Trends Over Time
        </Typography>
        <Skeleton variant="rectangular" height={400} />
      </Paper>
    );
  }

  // `Array.isArray`, not truthiness: GET /reports/hybrid-booking/trends returns
  // a WRAPPER ({ trends, filters }), and a `!data` check passes an object
  // straight into `.length`/`.map`. The API layer now unwraps it, but this page
  // has no error boundary, so a wrong shape must degrade to the empty state
  // rather than blank the screen.
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Booking Trends Over Time
        </Typography>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No booking trend data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  const chartData = data.map((trend) => ({
    week: trend.weekLabel,
    totalBookings: trend.totalBookings,
    confirmedBookings: trend.confirmedBookings,
    completionRate: trend.completionRate,
    averageHours: trend.averageHoursToBook,
  }));

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Booking Trends Over Time
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track total bookings, confirmed bookings, and completion rates week by week
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="week" />
          <YAxis yAxisId="left" label={{ value: 'Booking Count', angle: -90, position: 'insideLeft' }} />
          <YAxis
            yAxisId="right"
            orientation="right"
            label={{ value: 'Completion Rate (%)', angle: 90, position: 'insideRight' }}
            domain={[0, 100]}
          />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length > 0) {
                const data = payload[0].payload;
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2">{data.week}</Typography>
                    <Typography variant="body2">
                      Total Bookings: {data.totalBookings}
                    </Typography>
                    <Typography variant="body2">
                      Confirmed: {data.confirmedBookings}
                    </Typography>
                    <Typography variant="body2">
                      Completion Rate: {data.completionRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" display="block">
                      Avg Time to Book: {data.averageHours.toFixed(1)} hours
                    </Typography>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="totalBookings"
            name="Total Bookings"
            stroke="#4580E4"
            strokeWidth={2}
            dot={{ r: 4 }}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="confirmedBookings"
            name="Confirmed Bookings"
            stroke="#5cb399"
            strokeWidth={2}
            dot={{ r: 4 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="completionRate"
            name="Completion Rate (%)"
            stroke="#FFCE00"
            strokeWidth={2}
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default BookingTrendsChart;
